import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../Layouts/Loader/Loader";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import { useTableContext } from "../../context/TableContext";
import { useTreeContext } from "../../context/TreeDataContext";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { updateApplicationBlockChecksum } from "../../utils/configChecksum/setterFunctions/appblockConfig";
import { getInitialConfigForApplicationBlocks } from "../../utils/initialConfig/getAppBlock";
import ActivityCardRenderer from "./ActivityCardRenderer/ActivityCardRenderer";
import CalenderRenderer from "./CalenderRenderer/CalenderRenderer";
import ChartRenderer from "./ChartRenderer/ChartRenderer";
import PositionChartBlock from "./ChartRenderer/PositionChartBlock";
import DetailviewDesktopRenderer from "./DetailsViewRenderer/Desktop/DetailviewDesktopRenderer";
import KanbanRenderer from "./KanbanRenderer/KanbanRenderer";
import MapsViewRenderer from "./MapsViewRenderer";
import PivotChartRenderer from "./PivotChartRenderer/PivotChartRenderer";
import ProgressBarRenderer from "./ProgressBarRenderer/ProgressBarRenderer";
import QueryBlockRenderer from "./QueryBlockRenderer/QueryBlockRenderer";
import RecordsViewRenderer from "./RecordsViewRenderer/RecordsViewRenderer";
import { getFolderFileById, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";

import { convertGridToPixels } from "../FlexibleLayout/utils";

const extractDataFromBlock = (type, data) => {
  let rootConfig;

  if (type === "recordsview") {
    rootConfig = data.recordsView;
  } else if (type === "detailsview") {
    rootConfig = data.detailsView;
  } else if (type === "chart") {
    rootConfig = data.chart;
  } else if (type === "progress-bar") {
    rootConfig = data.progressBarConfig;
  } else {
    rootConfig = data;
  }

  return {
    rootConfig,
    lockData: data.lockData
  };
};

const extractTableTypeAndIdFromBlock = (type, data) => {
  let tableId = "";
  let tableType = "";
  if (type === "recordsview" || type === "pivotcharts") {
    tableId = data.config.layout.table_id;
    tableType = data.table_type;
  }
  if (type === "detailsview" || type === "activitycard") {
    tableId = data.data.table_id;
    tableType = data.table_type;
  }
  if (type === "chart" || type === "progress-bar" || type === "kanban" || type === "calendar") {
    tableId = data.config.table_id;
    tableType = data.table_type;
  }
  return { tableId, tableType };
};
const exractInfoFromSelected = (selectedBlock) => {
  const [device, type] = selectedBlock.split("/");
  return { device, type };
};
const ApplicationBlockViewer = () => {
  const {
    selectedAppBlock,
    setSelectedAppBlock,
    handleSaveApplicationBlock,
    applicationBlockData,
    setApplicationBlockData,
    setLayout,
    layoutData,
    nestedGridData,
    setCurrentBlockConfig,
    blockDataDispatch,
    setRecordsviewLayout,
    setLayoutConfig,
    setChartGlobalFilter,
    setLockData
  } = useAppBlocksContext();
  const [isLoader, setIsLoader] = useState(true);
  const { blockId } = useParams();
  const { blocksExplorerData, setBlocksExplorerData, setSelectedDevice } = useTreeContext();
  const navigate = useNavigate();
  const { getTableTypeData } = useTableContext();

  const stateSetter = (type, data, options) => {
    setApplicationBlockData(data);
    switch (type) {
      case "recordsview":
        if (data.device === "mobile") {
          setLayout(options.catalog.layout);
          layoutData.current = options.catalog.layoutData;
          nestedGridData.current = options.catalog.nestedGridData;
          setLayoutConfig(options.positionLayoutConfig);
          setRecordsviewLayout(data.config.layout);
        } else {
          const { catalog } = options;
          layoutData.current = catalog.layoutData;
          nestedGridData.current = catalog.nestedGridData;
          setLayout(catalog.layout);
          blockDataDispatch({ type: "SET_CONFIG", payload: data });
        }
        break;
      case "detailsview":
      case "activitycard":
        setLayout(options.elementsLayout.layout);
        layoutData.current = options.elementsLayout.layoutData;
        nestedGridData.current = options.elementsLayout.nestedGridData;
        if (data.device === "mobile") {
          setLayoutConfig(options.positionLayoutConfig);
        } else {
          setLayoutConfig(data.position_block_config);
        }
        break;
      case "chart":
        const { chartLayout } = options;
        setLayout(chartLayout.layout);
        layoutData.current = chartLayout.layoutData;
        const newConfigOfChart = {
          global_filters: data.config.global_filters,
          tiles: data.config.tiles,
          styles: data.config.styles
        };
        setChartGlobalFilter(newConfigOfChart);
        if (data.device === "mobile") {
          let newPositionedBlockConfig = convertGridToPixels(
            data.position_block_config || [],
            data.position_block_metadata?.grid_size
          );
          setLayoutConfig(newPositionedBlockConfig);
        } else {
          setLayoutConfig(data.position_block_config);
        }
        break;
      case "progress-bar":
      case "calendar":
      case "queryblocks":
        setCurrentBlockConfig(data.config);
        break;
      case "mapsview":
        setRecordsviewLayout(data.config.layout);
        break;
      case "kanban":
        setLayout(options.layout.layout);
        layoutData.current = options.layout.layoutData;
        nestedGridData.current = options.layout.nestedGridData;
        setCurrentBlockConfig(data.config);
        if (data.device === "mobile") {
          setLayoutConfig(options.positionLayoutConfig);
        } else {
          setLayoutConfig(data.position_block_config);
        }
        break;
      case "pivotcharts":
        setRecordsviewLayout(data.config.layout);
        setLayout(options.layout.layout);
        layoutData.current = options.layout.layoutData;
        break;
      default:
        break;
    }
  };

  const fetchSingleApplicationBlock = async () => {
    setIsLoader(true);
    setLayout([]);
    setCurrentBlockConfig(null);
    const { type } = exractInfoFromSelected(selectedAppBlock.parentData);
    try {
      const { data } = await axiosInstance.get(`/${type}/${selectedAppBlock.id}`);
      const { rootConfig, lockData } = extractDataFromBlock(type, data);
      const { tableId, tableType } = extractTableTypeAndIdFromBlock(type, rootConfig);
      let tableData = null;
      if (tableId) {
        tableData = await getTableTypeData(tableType, tableId);
      }
      updateApplicationBlockChecksum(type, rootConfig, true);
      const updatedConfig = getInitialConfigForApplicationBlocks(type, rootConfig, { tableData });
      // // updatedConfig.options.catalog;
      const storeConfig = updateApplicationBlockChecksum(type, updatedConfig.config);
      stateSetter(type, storeConfig, updatedConfig.options);
      setLockData(lockData);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleCloneApplicationBlock = async () => {
    try {
      await handleSaveApplicationBlock();
      // const blockName = selectedAppBlock.parentData.split("/")[1];
      // const deviceName = selectedAppBlock.parentData.split("/")[0];
      const { type, device } = exractInfoFromSelected(selectedAppBlock.parentData);
      const { data } = await axiosInstance.post(`${type}/${selectedAppBlock.id}/clone`);
      const newItem = fileFolderConfig.getFileConfig(
        data.id,
        data.name,
        `${data.device}/${type}`,
        true,
        true,
        data.table_type
      );
      const newTree = insertNewFileOrFolder(blocksExplorerData, { id: `${device}/${type}` }, newItem);
      setBlocksExplorerData([...newTree]);
      navigate(`../${data.id}`);
    } catch (error) {
      handleApiError(error);
    }
  };
  const renderApplicationBlock = () => {
    const { type } = exractInfoFromSelected(selectedAppBlock.parentData);
    switch (type) {
      case "recordsview":
        return (
          <RecordsViewRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "detailsview":
        return <DetailviewDesktopRenderer />;
      case "chart":
        if (applicationBlockData.is_positioned_block) {
          return <PositionChartBlock />;
        } else {
          return <ChartRenderer />;
        }
      case "mapsview":
        return (
          <MapsViewRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "activitycard":
        return (
          <ActivityCardRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "calendar":
        return (
          <CalenderRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "kanban":
        return (
          <KanbanRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "progress-bar":
        return (
          <ProgressBarRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      case "pivotcharts":
        return <PivotChartRenderer />;
      case "queryblocks":
        return (
          <QueryBlockRenderer
            applicationBlockData={applicationBlockData}
            handleSaveApplicationBlock={handleSaveApplicationBlock}
            handleCloneApplicationBlock={handleCloneApplicationBlock}
          />
        );
      default:
        return <h2>No Block Found</h2>;
    }
  };
  useEffect(() => {
    if (!blocksExplorerData || selectedAppBlock.id === blockId) return;
    setIsLoader(true);
    const file = getFolderFileById(blocksExplorerData, blockId);
    setApplicationBlockData({ id: "" });
    if (file) {
      setSelectedAppBlock({
        id: file.id,
        name: file.name,
        parentData: file.parentData,
        table_id: file.fileInfo?.table_id,
        activity_id: file.fileInfo?.activity_id
      });
      setSelectedDevice(file.parentData.split("/")[0]);
    } else {
      navigate("../");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId, blocksExplorerData]);
  useEffect(() => {
    if (selectedAppBlock?.id === blockId) {
      fetchSingleApplicationBlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppBlock, selectedAppBlock?.id]);
  if (selectedAppBlock?.id !== blockId) return;
  return (
    <div className="content_main_section">
      {isLoader ? <Loader /> : selectedAppBlock?.id && applicationBlockData.id && renderApplicationBlock()}
    </div>
  );
};

export default ApplicationBlockViewer;
